import React, { useState, useEffect } from 'react';
import { Alert, AlertTitle, Box, Chip, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { SocialPlatform } from '../types';
import { getPlatformName, getPlatformColor } from '../config/platforms';
import { GUIDANCE } from '../config/postingSchedule';

const API_URL = process.env.REACT_APP_API_URL || '';

interface DayPost {
  id: string;
  excerpt: string;
  platforms: string[];
  status: string;
  at: string;
}

/** Local YYYY-MM-DD. toISOString() would roll an evening back a day in western timezones. */
const localDay = (d: Date) => {
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 10);
};

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

/**
 * Says what is already going out on the chosen day, per platform.
 *
 * Exists because scheduling onto an occupied day said nothing at all: Ian added a post to a
 * day that already had others, saw no acknowledgement, and read it as the post having
 * vanished. It had saved — nothing on the page told him so.
 *
 * Advisory, never blocking, matching TimingAdvice: two posts in a day is sometimes right, and
 * on X it is normal. What it adds is the per-network rule from the posting schedule, so
 * "Instagram: one feed post a day is the ceiling" appears at the moment the decision is made
 * rather than on a page nobody is looking at.
 *
 * Only counts a collision on a platform being posted to. A LinkedIn post tomorrow is not a
 * reason to warn about an Instagram one.
 */
export const SameDayWarning: React.FC<{
  platforms: SocialPlatform[];
  when: Date | null;
  /** Hide a post being edited, or it warns about itself. */
  excludePostId?: string;
}> = ({ platforms, when, excludePostId }) => {
  const { token } = useAuth();
  const [posts, setPosts] = useState<DayPost[]>([]);

  useEffect(() => {
    if (!token || !when || platforms.length === 0) {
      setPosts([]);
      return;
    }
    let cancelled = false;
    const day = localDay(when);
    // The day boundaries as the viewer sees them, so the server does no timezone guessing.
    const start = new Date(when); start.setHours(0, 0, 0, 0);
    const end = new Date(when); end.setHours(23, 59, 59, 999);

    fetch(`${API_URL}/api/posts/on-day?date=${day}&from=${start.toISOString()}&to=${end.toISOString()}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : { posts: [] }))
      .then(d => { if (!cancelled) setPosts(d.posts || []); })
      // A failed lookup must never block composing — the warning is a courtesy.
      .catch(() => { if (!cancelled) setPosts([]); });

    return () => { cancelled = true; };
  }, [token, when, platforms, excludePostId]);

  if (!when || platforms.length === 0) return null;

  const others = posts.filter(p => p.id !== excludePostId);
  // Only a platform being posted to counts as a collision.
  const clashing = others.filter(p => p.platforms.some(pl => platforms.includes(pl as SocialPlatform)));
  if (clashing.length === 0) return null;

  const affected = platforms.filter(pl => clashing.some(p => p.platforms.includes(pl)));

  return (
    <Alert severity="info" sx={{ mt: 2 }}>
      <AlertTitle>
        Already going out on {when.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
      </AlertTitle>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.5 }}>
        {clashing.map(p => (
          <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 64 }}>
              {formatTime(p.at)}
            </Typography>
            {p.platforms.map(pl => (
              <Chip
                key={pl}
                size="small"
                label={getPlatformName(pl)}
                sx={{
                  height: 20,
                  bgcolor: getPlatformColor(pl),
                  color: '#fff',
                  opacity: platforms.includes(pl as SocialPlatform) ? 1 : 0.45,
                }}
              />
            ))}
            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 260 }}>
              {p.excerpt}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* The per-network rule from the posting schedule, at the moment it is relevant. */}
      {affected.map(pl => {
        const rule = GUIDANCE[pl]?.sameDay;
        if (!rule || rule === '—') return null;
        return (
          <Typography key={pl} variant="body2" sx={{ mb: 0.5 }}>
            <strong>{getPlatformName(pl)}:</strong> {rule}
          </Typography>
        );
      })}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        This is a note, not a block — you can schedule it anyway.
      </Typography>
    </Alert>
  );
};

export default SameDayWarning;
